import React, { useState, useEffect } from 'react';
import { ArrowLeft, MessageCircle, Send, Lock, Unlock, Edit2, Trash2, Save, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Candidate, Evaluation, EvaluationComment } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

interface CandidateCommentsProps {
  evaluation: Evaluation;
  candidate: Candidate;
  onBack: () => void;
}

export default function CandidateComments({
  evaluation,
  candidate,
  onBack
}: CandidateCommentsProps) {
  const [comments, setComments] = useState<EvaluationComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSecretComment, setIsSecretComment] = useState(false);
  const [isSecretReply, setIsSecretReply] = useState(false);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editSecret, setEditSecret] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluatorInfo, setEvaluatorInfo] = useState<{name: string, username?: string} | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [evaluation.id]);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }

      // Load evaluator info
      const { data: evaluatorData } = await supabase
        .from('evaluators')
        .select('name, username')
        .eq('id', evaluation.evaluator_id)
        .single();

      if (evaluatorData) {
        setEvaluatorInfo(evaluatorData);
      }

      // Load comments
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const { data, error } = await supabase
        .from('evaluation_comments')
        .select('*')
        .eq('evaluation_id', evaluation.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(data || []);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSubmitComment = async () => {
    if ((!newComment.trim() && !newTopic.trim())) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluation.id,
          user_id: user.id,
          user_type: 'candidate',
          content: newComment.trim() || `新しいトピック: ${newTopic.trim()}`,
          is_secret: isSecretComment,
          parent_comment_id: null,
          topic: newTopic.trim() || 'general'
        });

      if (error) throw error;

      setNewComment('');
      setNewTopic('');
      setIsSecretComment(false);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleSubmitReply = async (parentId: string, topic: string) => {
    if (!replyContent.trim()) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluation.id,
          user_id: user.id,
          user_type: 'candidate',
          content: replyContent.trim(),
          is_secret: isSecretReply,
          parent_comment_id: parentId,
          topic: topic
        });

      if (error) throw error;

      setReplyContent('');
      setReplyingTo(null);
      setIsSecretReply(false);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleEditComment = (comment: EvaluationComment) => {
    if (comment.user_id !== currentUserId) return;
    setEditingComment(comment.id);
    setEditContent(comment.content);
    setEditSecret(comment.is_secret);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editContent.trim()) return;

    setCommentLoading(true);
    try {
      const { error } = await supabase
        .from('evaluation_comments')
        .update({
          content: editContent.trim(),
          is_secret: editSecret
        })
        .eq('id', commentId)
        .eq('user_id', currentUserId!);

      if (error) throw error;

      setEditingComment(null);
      setEditContent('');
      setEditSecret(false);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditContent('');
    setEditSecret(false);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('このコメントを削除しますか？')) return;

    setCommentLoading(true);
    try {
      const { error } = await supabase
        .from('evaluation_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', currentUserId!);

      if (error) throw error;

      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const organizeComments = (comments: EvaluationComment[]) => {
    const topicGroups: { [key: string]: EvaluationComment[] } = {};
    const replies: { [key: string]: EvaluationComment[] } = {};

    comments.forEach(comment => {
      if (comment.parent_comment_id) {
        if (!replies[comment.parent_comment_id]) {
          replies[comment.parent_comment_id] = [];
        }
        replies[comment.parent_comment_id].push(comment);
      } else {
        const topic = comment.topic || 'general';
        if (!topicGroups[topic]) {
          topicGroups[topic] = [];
        }
        topicGroups[topic].push(comment);
      }
    });

    return { topicGroups, replies };
  };

  const getCommentAuthorName = (comment: EvaluationComment) => {
    if (comment.user_type === 'candidate') {
      return candidate.username || candidate.name;
    } else if (comment.user_type === 'evaluator' && evaluatorInfo) {
      return evaluatorInfo.username || evaluatorInfo.name;
    }
    return comment.user_type === 'candidate' ? '研究者' : '参加者';
  };

  const formatCommentTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ja-JP', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('ja-JP', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const renderComment = (comment: EvaluationComment, allReplies: { [key: string]: EvaluationComment[] }, level = 0) => (
    <div key={comment.id} className={`${level > 0 ? 'ml-8 border-l-2 border-primary/30 pl-6' : ''} mb-4`}>
      <div className="flex justify-start mb-4">
        <div
          className={`max-w-[85%] rounded-lg p-4 shadow-sm transition-all duration-200 hover:shadow-md ${
            comment.user_type === 'evaluator'
              ? 'bg-blue-50 border border-blue-200 text-blue-900'
              : 'bg-green-50 border border-green-200 text-green-900'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                comment.user_type === 'evaluator' ? 'bg-blue-500' : 'bg-green-500'
              }`} />
              <span className="text-sm font-semibold">
                {getCommentAuthorName(comment)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {comment.is_secret && (
                <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                  <Lock className="h-3 w-3" />
                  <span className="text-xs font-medium">プライベート</span>
                </div>
              )}
              <span className="text-xs text-muted-foreground">
                {formatCommentTime(comment.created_at)}
              </span>
            </div>
          </div>
          
          {editingComment === comment.id ? (
            <div className="space-y-3">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={3}
                className="resize-none text-sm"
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-secret-${comment.id}`}
                    checked={editSecret}
                    onCheckedChange={(checked) => setEditSecret(!!checked)}
                  />
                  <Label htmlFor={`edit-secret-${comment.id}`} className="text-sm cursor-pointer">
                    プライベートコメント
                  </Label>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelEdit}
                  >
                    <X className="h-3 w-3 mr-1" />
                    キャンセル
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleSaveEdit(comment.id)}
                    disabled={!editContent.trim() || commentLoading}
                  >
                    <Save className="h-3 w-3 mr-1" />
                    保存
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className={`mb-3 ${comment.is_secret ? 'bg-gray-100 border border-gray-300 rounded p-3' : ''}`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere">
                  {comment.content}
                </p>
              </div>
              
              <div className="flex justify-between items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-xs hover:bg-primary/10"
                  onClick={() => setReplyingTo(comment.id)}
                >
                  <MessageCircle className="h-3 w-3 mr-1" />
                  返信する
                </Button>
                
                {comment.user_id === currentUserId && (
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => handleEditComment(comment)}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs text-destructive hover:text-destructive"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Reply form */}
      {replyingTo === comment.id && (
        <div className="mb-4 ml-4">
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">返信を投稿</span>
            </div>
            <Textarea
              placeholder="返信内容を入力してください..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="secret-reply"
                  checked={isSecretReply}
                  onCheckedChange={(checked) => setIsSecretReply(!!checked)}
                />
                <Label htmlFor="secret-reply" className="flex items-center space-x-2 cursor-pointer text-sm">
                  <span className="text-muted-foreground">プライベート返信</span>
                  {isSecretReply && (
                    <Lock className="h-3 w-3 text-orange-500" />
                  )}
                </Label>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent('');
                    setIsSecretReply(false);
                  }}
                >
                  キャンセル
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSubmitReply(comment.id, comment.topic)}
                  disabled={!replyContent.trim() || commentLoading}
                >
                  {commentLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  返信を投稿
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Nested replies */}
      <div className="space-y-2">
        {allReplies[comment.id]?.map(reply => renderComment(reply, allReplies, level + 1))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          一覧に戻る
        </Button>
      </div>

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Participant Info */}
      <Card>
        <CardHeader>
          <CardTitle>コメント詳細</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">参加者</span>
              <span className="font-medium">{evaluatorInfo?.name || '匿名参加者'}</span>
            </div>
            <div className="flex items-center space-x-4">
              {evaluation.liked && (
                <Badge variant="secondary" className="text-red-500">
                  ❤️ いいね
                </Badge>
              )}
              {evaluation.want_to_participate && (
                <Badge variant="secondary" className="text-green-600">
                  🙋 参加希望
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageCircle className="h-5 w-5 mr-2" />
            コメント・質問
          </CardTitle>
          <CardDescription>
            参加者とのやり取りができます。トピックごとに整理され、返信も可能です。原則として全員が閲覧可能なディスカッションの場です。プライベートなやり取りが必要な場合はチェックボックスをご利用ください。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Topic-based Comments */}
            <div className="space-y-8 max-h-none overflow-visible md:max-h-[700px] md:overflow-y-auto">
            {comments.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                まだコメントがありません
              </p>
            ) : (
              (() => {
                const { topicGroups, replies } = organizeComments(comments);
                return Object.entries(topicGroups).map(([topic, topicComments]) => (
                  <div key={topic} className="border border-border rounded-lg p-6 bg-card shadow-sm">
                    <div className="mb-6 pb-3 border-b border-border">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-lg text-foreground flex items-center">
                          <MessageCircle className="h-5 w-5 text-primary mr-2" />
                          {topic === 'general' ? '💬 一般的な質問・コメント' : `📋 ${topic}`}
                        </h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setNewTopic(topic === 'general' ? '' : topic);
                            setNewComment('');
                            document.getElementById('new-comment-input')?.focus();
                          }}
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          コメント追記
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {topicComments.length}件のコメント・返信
                      </p>
                    </div>
                    <div className="space-y-4">
                      {topicComments.map(comment => 
                        renderComment(comment, replies, 0)
                      )}
                    </div>
                  </div>
                ));
              })()
            )}
          </div>

          {/* New Comment/Topic Input */}
          <div className="space-y-4 border-t border-border pt-6 bg-muted/20 rounded-lg p-4">
            <h5 className="font-semibold text-base text-foreground flex items-center">
              <Send className="h-4 w-4 text-primary mr-2" />
              新しいコメント・返信を投稿
            </h5>
            <div className="space-y-3">
              <Label htmlFor="new-topic" className="text-sm font-medium text-foreground">
                新しいトピック（オプション）
              </Label>
              <Input
                id="new-topic"
                placeholder="例: 研究手法について、データ収集について..."
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
              />
            </div>
            <Textarea
              id="new-comment-input"
              placeholder="コメントや返信を入力してください（トピックのみの場合は空でも可）..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={4}
              className="resize-none"
            />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="secret-comment"
                  checked={isSecretComment}
                  onCheckedChange={(checked) => setIsSecretComment(!!checked)}
                />
                <Label htmlFor="secret-comment" className="flex items-center space-x-2 cursor-pointer">
                  <span className="text-sm text-muted-foreground">
                    公開したくないプライベートなやり取りはこちらをチェックしてください
                  </span>
                  {isSecretComment && (
                    <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                      <Lock className="h-3 w-3" />
                      <span className="text-xs font-medium">プライベート</span>
                    </div>
                  )}
                </Label>
              </div>
              
                <Button
                  onClick={handleSubmitComment}
                  disabled={(!newComment.trim() && !newTopic.trim()) || commentLoading}
                >
                  {commentLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  投稿する
                </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
